"use client";

import Image from "next/image";
import Button from "@/components/common/button";
import BlurFadeText from "@/components/common/blurFadeText";
import FAIcon from "@/components/icons/faIcon";
import { BackgroundGradientAnimation } from "@/components/ui/background-gradient-animation";
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { useRef } from "react";

const SHELL_SPRING = {
  stiffness: 108,
  damping: 24,
  mass: 0.86,
};

export default function ImmersivePanelSection({
  animate = false,
  animateText = false,
  image = "/images/background/cta-section.webp",
  backgroundAlt,
  title,
  description,
  buttonText = "Get in touch",
  buttonHref = "/contact#contact-form",
  showButton = true,
  priority = false,
  expandOnScroll = false,
  sectionClassName = "",
  panelClassName = "",
  contentClassName = "",
  buttonClassName = "",
  heightClass = "h-110",
  expandScaleXStart = 0.76,
  expandStart = 0.3,
  expandEnd = 0.88,
  revealStart = 0.58,
  revealEnd = 0.9,
}) {
  const sectionRef = useRef(null);
  const shouldAnimateWords = animateText && !expandOnScroll;
  const useReducedBackdropEffects = animate && expandOnScroll;
  const staticProgress = useMotionValue(1);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "start center"],
  });

  const progress = expandOnScroll ? scrollYProgress : staticProgress;
  const expansion = useSpring(
    useTransform(progress, [expandStart, expandEnd], [0, 1], {
      clamp: true,
    }),
    SHELL_SPRING,
  );

  const reveal = useSpring(
    useTransform(progress, [revealStart, revealEnd], [0, 1], {
      clamp: true,
    }),
    SHELL_SPRING,
  );

  const shellWidthPercent = useTransform(
    expansion,
    [0, 1],
    [((1 - expandScaleXStart) / 2) * 100, 0],
  );
  const shellClipPath = useMotionTemplate`inset(0 ${shellWidthPercent}% 0 ${shellWidthPercent}% round 1rem)`;
  const backdropScale = useTransform(
    expansion,
    [0, 1],
    [useReducedBackdropEffects ? 1.08 : 1.18, 1],
  );
  const overlayOpacity = useTransform(expansion, [0, 1], [0.72, 0.42]);

  const contentOpacity = useTransform(reveal, [0, 1], [0, 1]);
  const contentScale = useTransform(reveal, [0, 1], [0.94, 1]);
  const contentBlur = useTransform(
    reveal,
    [0, 1],
    [expandOnScroll ? 0 : 14, 0],
  );
  const contentFilter = useMotionTemplate`blur(${contentBlur}px)`;
  const contentMotionStyle = expandOnScroll
    ? {
        opacity: contentOpacity,
        scale: contentScale,
      }
    : {
        opacity: contentOpacity,
        scale: contentScale,
        filter: contentFilter,
      };

  return (
    <section
      ref={sectionRef}
      className={`relative flex w-full justify-center overflow-hidden ${
        expandOnScroll ? " items-end " : "items-center py-0"
      } ${sectionClassName}`}
    >
      <motion.div
        style={{
          clipPath: shellClipPath,
          willChange: "clip-path",
          contain: "paint",
        }}
        className={`relative w-full overflow-hidden rounded-lg bg-primary ${heightClass} ${panelClassName}`}
      >
        {animate ? (
          <motion.div
            style={{ scale: backdropScale, willChange: "transform" }}
            className="absolute inset-0"
          >
            <BackgroundGradientAnimation
              reduceEffects={useReducedBackdropEffects}
              interactive={false}
              containerClassName="absolute inset-0"
              className="h-full w-full"
              gradientBackgroundStart="rgb(240, 240, 240)" // #FEC84F
              gradientBackgroundEnd="rgb(232, 176, 42)" // deeper golden amber
              firstColor="254, 200, 79" // primary gold
              secondColor="255, 214, 150" // soft warm highlight
              thirdColor="245, 190, 70" // balanced golden glow
              fourthColor="224, 164, 38" // richer darker gold
              fifthColor="255, 186, 92" // warm champagne bronze
              pointerColor="255, 208, 196"
              size="90%"
              blendingValue="hard-light"
            />
          </motion.div>
        ) : (
          <motion.div
            style={{ scale: backdropScale, willChange: "transform" }}
            className="absolute inset-0"
          >
            <Image
              src={image}
              alt={backgroundAlt || description || title}
              fill
              priority={priority}
              className="object-cover opacity-5 grayscale contrast-130"
            />
          </motion.div>
        )}

        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.05),rgba(0,0,0,0.0)_38%,rgba(0,0,0,0.2))]"
        />

        <motion.div
          style={contentMotionStyle}
          className={`relative z-10 flex h-full flex-col items-center justify-center px-6 text-center text-white sm:px-10 ${contentClassName}`}
        >
          <div className="mx-auto flex max-w-5xl flex-col items-center gap-5 sm:gap-6">
            <h2 className="max-w-5xl text-5xl font-semibold leading-tight tracking-[-0.05em] text-white sm:text-6xl lg:text-[4.7rem] lg:leading-[0.96]">
              {shouldAnimateWords ? (
                <BlurFadeText text={title} blur={12} y={22} amount={0.35} />
              ) : (
                title
              )}
            </h2>

            {description ? (
              <p className="max-w-2xl text-lg  text-accent/76 font-semibold sm:text-xl">
                {shouldAnimateWords ? (
                  <BlurFadeText
                    text={description}
                    delay={0.08}
                    blur={9}
                    y={16}
                    amount={0.35}
                  />
                ) : (
                  description
                )}
              </p>
            ) : null}

            {showButton ? (
              <div className="mt-4">
                <Button
                  href={buttonHref}
                  text={buttonText}
                  variant="secondary"
                  endIcon={<FAIcon icon="arrow-right" size={14} noMargin />}
                />
              </div>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
